/* ================================================================
   MATRIX FOREX — GLOBAL RATE UTILITY
   Single source of truth for live rates across the entire theme.
   ----------------------------------------------------------------
   Usage:
     window.MatrixRates.onReady(function (rates) { ... });
     window.MatrixRates.getBuyRate('USD');
     window.MatrixRates.getSellRate('EUR');
   ================================================================ */
(function (global) {
  'use strict';

  var API_URL = 'https://proud-pine-9f37.tanujrajput-dev.workers.dev/';

  var ALLOWED = [
    'AED','AUD','BDT','BHD','CAD','CHF','CNY','CZK',
    'DKK','EGP','EUR','GBP','HKD','IDR','JPY','KRW',
    'KWD','LKR','MUR','MVR','MYR','NOK','NZD','OMR',
    'PHP','PLN','QAR','RUB','SAR','SEK','SGD','THB',
    'TRY','TWD','USD','VND','ZAR'
  ];
  var ALLOWED_SET = {};
  ALLOWED.forEach(function (c) { ALLOWED_SET[c] = true; });

  /* ⚠ OMR, BHD and KWD (added Aug 2026) carry PROVISIONAL margins, not desk
     figures. They are derived at 2.36% of the interbank ask — the effective
     rate SAR runs at (0.60 on an ask of 25.42), chosen because SAR is the
     closest comparable: Gulf, USD-pegged, cash-note corridor. The other Gulf
     rows disagree with each other (AED 3.85%, QAR 0.84%), so there was no
     single house rate to copy.

     Because these three units are worth ~10x a dirham (OMR ₹248, BHD ₹253,
     KWD ₹311), a margin that looks small in rupees is still a large
     percentage — 1.00 here would be well under cost.

     THE DESK MUST CONFIRM THESE BEFORE fx14 IS PUBLISHED. */
  /* Tier 2 (PLN, PHP, TRY, TWD, MVR, EGP, added Aug 2026) quoted the raw interbank ask until 5 Oct 2026.
     On the owner's instruction they now carry about 3% of the interbank ask (the last rows of MARGINS), so
     every page quotes Matrix's selling rate rather than interbank. They are still not branch stock: their
     pages carry sourcing_note, and the desk confirms availability and the exact rate before booking.
     CZK: buy margin set to 2.09 on 5 Oct 2026 (owner: about ₹6.50 a koruna at that day's ask); it moves with the ask.
     They have no CARD_MARGINS rows: they are not on the order form and are not sold on a card. */
  var MARGINS = {
    AED:{buy:0.90,sell:-0.80},  AUD:{buy:1.00,sell:-0.80},
    BDT:{buy:0.07,sell:-0.04},  BHD:{buy:5.98,sell:-5.98},
    CAD:{buy:1.50,sell:-1.0},   CHF:{buy:1.80,sell:-0.80},
    CNY:{buy:0.70,sell:-0.30},
    CZK:{buy:2.09,sell:-0.05},  DKK:{buy:1.70,sell:-0.04},
    EUR:{buy:1.25,sell:-0.80},  GBP:{buy:1.30,sell:-0.80},
    HKD:{buy:0.70,sell:-0.20},  IDR:{buy:0.0008,sell:-0.00015},
    JPY:{buy:0.025,sell:-0.006}, KRW:{buy:0.008,sell:-0.0027},
    KWD:{buy:7.33,sell:-7.33},  LKR:{buy:0.12,sell:-0.12},
    MUR:{buy:0.30,sell:-0.25},  MYR:{buy:1.40,sell:-0.45},
    NOK:{buy:0.90,sell:-0.25},  NZD:{buy:0.90,sell:-0.40},
    OMR:{buy:5.85,sell:-5.85},  QAR:{buy:1.04,sell:-0.60},
    RUB:{buy:0.25,sell:-0.02},  SAR:{buy:0.90,sell:-0.60},
    SEK:{buy:0.90,sell:-0.20},  SGD:{buy:1.10,sell:-0.30},
    THB:{buy:0.12,sell:-0.10},  USD:{buy:0.95,sell:-0.80},
    VND:{buy:0.0006,sell:-0.00003}, ZAR:{buy:0.35,sell:-0.35},
    EGP:{buy:0.06,sell:-0.06},  MVR:{buy:0.19,sell:-0.19},
    PHP:{buy:0.05,sell:-0.05},  PLN:{buy:0.74,sell:-0.74},
    TRY:{buy:0.06,sell:-0.06},  TWD:{buy:0.09,sell:-0.09}
  };

  /* ── FOREX-CARD RATE BOOK ────────────────────────────────────────
     Flat rupee margin over the interbank ask, per currency — the card
     twin of MARGINS.buy above. Cards are cheaper to fulfil than notes
     (no inventory, no denominations), so each value should sit between
     0 (the raw API ask) and that currency's `buy` margin.

     Values are the desk's hand-set margins (Aug 2026 update). CZK, LKR,
     MUR, QAR and RUB were not in that update and still carry the launch
     value (half their cash margin).

     JPY is 0.0181 on the desk's explicit confirmation, and it sits ABOVE
     the 0.015 cash margin — the one currency where the card prices worse
     than notes. The form handles this honestly: the "Best rate" badge on
     the Forex Card pill auto-hides whenever card >= cash (see
     syncServiceRates() in hero-form.liquid), so JPY shows plain rates
     with no false claim. Margins here are per 1 JPY.

     ⚠ AFTER EDITING ANY VALUE: bump CACHE_KEY below, or anyone holding
       a warm cache keeps the old rate for up to 10 minutes. */
  var CARD_MARGINS = {
    AED:0.11,    AUD:0.11,
    BDT:0.02,    BHD:1.80,
    CAD:0.11,    CHF:0.17,
    CNY:0.12,    KWD:2.20,
    OMR:1.75,
    CZK:0.03,    DKK:0.10,
    EUR:0.43,    GBP:0.19,
    HKD:0.15,    IDR:0.00008,
    JPY:0.0150,  KRW:0.0013,
    LKR:0.015,   MUR:0.01,
    MYR:0.21,    NOK:0.11,
    NZD:0.08,    QAR:0.06,
    RUB:0.016,   SAR:0.18,
    SEK:0.08,    SGD:0.11,
    THB:0.05,    USD:0.15,
    VND:0.00005, ZAR:0.10
  };
  /* Safety net only — a currency missing from CARD_MARGINS prices at half
     its cash margin, which is what every launch value above equals. */
  var CARD_MARGIN_FACTOR = 0.5;

  /* ── STUDENT-REMITTANCE RATE BOOK ────────────────────────────────
     Flat rupee markup over the interbank mid, per currency. A currency
     is quotable for remittance IF AND ONLY IF it has a row here —
     REMIT_ONLY is derived from these keys, so adding a currency to the
     desk is one line in this table.

     All rows launch at the old flat REMIT_ADD figure (0.02), so this
     restructure changed no live rate. Every current row prices to
     2 decimals (see precision()); if a 4- or 6-decimal currency is ever
     added here, pick its markup with that precision in mind.

     REMIT_PCT is the older proportional lever, left at 0. It compounds
     with the per-currency markup if both are set. Buy/sell margins are
     untouched by either.

     NB this is a markup on the exchange rate, which the hero headline
     ("Transfer at zero markup") and the pricing copy both currently deny.

     ⚠ AFTER EDITING ANY VALUE (or adding/removing a row): bump CACHE_KEY
       below, or anyone holding a warm cache keeps the old table for up
       to 10 minutes. */
  var REMIT_PCT = 0;
  var REMIT_MARGINS = {
    USD:0.02, EUR:0.02, GBP:0.02, CAD:0.02,
    AUD:0.02, SGD:0.02, NZD:0.02
  };
  var REMIT_ONLY = Object.keys(REMIT_MARGINS);

  var FLAGS = {
    AED:'🇦🇪', AUD:'🇦🇺', BDT:'🇧🇩', BHD:'🇧🇭', CAD:'🇨🇦', CHF:'🇨🇭',
    CNY:'🇨🇳', CZK:'🇨🇿', DKK:'🇩🇰', EUR:'🇪🇺', GBP:'🇬🇧', HKD:'🇭🇰',
    IDR:'🇮🇩', JPY:'🇯🇵', KRW:'🇰🇷', KWD:'🇰🇼', LKR:'🇱🇰', MUR:'🇲🇺',
    MYR:'🇲🇾', NOK:'🇳🇴', NZD:'🇳🇿', OMR:'🇴🇲', QAR:'🇶🇦', RUB:'🇷🇺',
    SAR:'🇸🇦', SEK:'🇸🇪', SGD:'🇸🇬', THB:'🇹🇭', USD:'🇺🇸', VND:'🇻🇳',
    ZAR:'🇿🇦', INR:'🇮🇳'
  };

/* v4 -> v5 added REMIT_ADD; v5 -> v6 repriced it 0.03 -> 0.02; v6 -> v7 added
   the forex-card column; v7 -> v8 the desk's Aug 2026 hand-set card margins
   (JPY held, see CARD_MARGINS); v8 -> v9 added OMR, BHD and KWD; v9 -> v10 added the Tier 2 six. The cache
   stores the table *after* margins, so every change to a margin needs a new
   key here — otherwise anyone holding a warm cache keeps seeing the previous
   rates until the 10-minute TTL lapses. */
var CACHE_KEY = 'mx_rates_v11';
 var CACHE_TTL = 10 * 60 * 1000;

  var rateTable = {};
  var loaded = false;
  var loading = null;
  var listeners = [];

  /* Feed quote units. The upstream feed quotes JPY per 100 units; every other
     currency per 1. Normalise at ingestion so every surface works in
     "rupees per 1 unit". Confirmed against: the converter fallback table
     (INR:{JPY:1.6736} => 1 JPY = Rs 0.5976), precision('JPY') = 4, and
     MIN_QTY JPY:10000 (a ~Rs 6,080 minimum order). Margins below are therefore
     per 1 unit: JPY buy is 0.015, not 1.50. */
  var UNITS = { JPY: 100 };

  function precision(code) {
    if (code === 'VND' || code === 'IDR') return 6;
    if (code === 'JPY' || code === 'KRW' || code === 'LKR' || code === 'BDT') return 4;
    return 2;
  }

  function applyMargins(list) {
    var table = {};
    list.forEach(function (item) {
      if (!item || !item.CurrencyCode) return;
      var code = String(item.CurrencyCode).toUpperCase();
      if (!ALLOWED_SET[code]) return;
      var u   = UNITS[code] || 1;
      var bid = parseFloat(item.Bid || 0) / u;
      var ask = parseFloat(item.Ask || 0) / u;
      var mid = (bid + ask) / 2 || ask || bid;
      if (!mid) return;
      var m = MARGINS[code] || { buy: 0, sell: 0 };
      /* Quote remittance off a mid already rounded to display precision, so the
         rate on screen is exactly what the arithmetic gives — rounding the two
         independently made the margin look inconsistent between surfaces. */
      var midR = +mid.toFixed(precision(code));
      var cardM = (CARD_MARGINS[code] != null) ? CARD_MARGINS[code] : m.buy * CARD_MARGIN_FACTOR;
      table[code] = {
        buy:  +(ask + m.buy).toFixed(6),
        card: +(ask + cardM).toFixed(6),
        sell: +(bid + m.sell).toFixed(6),
        remit: (REMIT_MARGINS[code] == null) ? null
               : +(midR * (1 + REMIT_PCT) + REMIT_MARGINS[code]).toFixed(precision(code)),
        mid:  +mid.toFixed(6),
        bid:  +bid.toFixed(6),
        ask:  +ask.toFixed(6)
      };
    });
    return table;
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.ts || !obj.table) return null;
      if (Date.now() - obj.ts > CACHE_TTL) return null;
      return obj.table;
    } catch (e) { return null; }
  }

  function writeCache(table) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), table: table })); } catch (e) {}
  }

  function notify() {
    listeners.forEach(function (cb) { try { cb(rateTable); } catch (e) { console.error(e); } });
    document.dispatchEvent(new CustomEvent('matrix-rates-ready', { detail: { rates: rateTable } }));
  }

  function fetchLive() {
    if (loading) return loading;
    loading = fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.error) throw new Error(data.details || 'Rate API error');
        var list = Array.isArray(data) ? data : (data.rates || []);
        var table = applyMargins(list);
        if (!Object.keys(table).length) throw new Error('Empty rate list');
        rateTable = table;
        writeCache(rateTable);
        loaded = true;
        notify();
        return rateTable;
      })
      .catch(function (e) {
        console.warn('[MatrixRates] live fetch failed', e);
        if (Object.keys(rateTable).length) { notify(); return rateTable; }
        return null;
      });
    return loading;
  }

  function init() {
    var cached = readCache();
    if (cached) {
      rateTable = cached;
      loaded = true;
      setTimeout(notify, 0);
    }
    fetchLive();
  }

  global.MatrixRates = {
    API_URL: API_URL,
    ALLOWED: ALLOWED,
    MARGINS: MARGINS,
    FLAGS: FLAGS,
    isReady: function () { return loaded; },
    getAll: function () { return rateTable; },
    get: function (code) { return rateTable[code] || null; },
    REMIT_PCT: REMIT_PCT,
    REMIT_ONLY: REMIT_ONLY,
    REMIT_MARGINS: REMIT_MARGINS,
    CARD_MARGINS: CARD_MARGINS,
    CARD_MARGIN_FACTOR: CARD_MARGIN_FACTOR,
    getBuyRate:  function (code) { return rateTable[code] ? rateTable[code].buy  : null; },
    /* Forex-card load rate. Falls back to the cash buy rate if a stale cached
       table (pre-v7) is ever in play, so callers never see undefined. */
    getCardRate: function (code) {
      var rec = rateTable[code]; if (!rec) return null;
      return (rec.card != null) ? rec.card : rec.buy;
    },
    getSellRate: function (code) { return rateTable[code] ? rateTable[code].sell : null; },
    getMidRate:  function (code) { return rateTable[code] ? rateTable[code].mid  : null; },
    getRemitRate:function (code) { return rateTable[code] ? rateTable[code].remit: null; },
    flag: function (code) { return FLAGS[code] || ''; },
    precision: precision,
    formatRate: function (code, mode) {
      var rec = rateTable[code]; if (!rec) return '--';
      var v = mode === 'sell' ? rec.sell
            : mode === 'card' ? ((rec.card != null) ? rec.card : rec.buy)
            : rec.buy;
      return v.toFixed(precision(code));
    },
    convert: function (code, amount, mode) {
      var rec = rateTable[code]; if (!rec) return 0;
      var r = mode === 'sell' ? rec.sell
            : mode === 'card' ? ((rec.card != null) ? rec.card : rec.buy)
            : rec.buy;
      return (parseFloat(amount) || 0) * r;
    },
    onReady: function (cb) {
      if (typeof cb !== 'function') return;
      listeners.push(cb);
      if (loaded) setTimeout(function () { cb(rateTable); }, 0);
    },
    refresh: function () { loading = null; return fetchLive(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window);
